import { useTranslation } from "@sagot/i18n";
import { ChoiceChips } from "@sagot/ui";
import { Controller, useFormContext } from "react-hook-form";
import { useAgentConfig, useAudienceLabels } from "../shared/queries";
import type { ActionFormValues } from "./schema-model";

const CODES = [1, 2, 3, 4] as const;

interface AudienceFieldProps {
  agentId: string;
  disabled: boolean;
  /** "Talk to a person" is for everyone: nothing to choose. */
  locked: boolean;
}

/** "Who can use this action": audience levels; the agent's own "Who can chat" limit applies on top. */
export function AudienceField({ agentId, disabled, locked }: AudienceFieldProps) {
  const { t } = useTranslation();
  const form = useFormContext<ActionFormValues>();
  const label = useAudienceLabels();
  const agent = useAgentConfig(agentId);
  if (locked) return <p className="rounded-md bg-surface-2 p-3 text-xs text-fg-muted">{t("build.actions.editor.audienceEveryone")}</p>;
  const levels = (agent.data?.audiences ?? []).map(label).join(", ");
  return (
    <div>
      <div className="text-sm font-semibold" id="action-audience-label">{t("build.actions.editor.audience")}</div>
      <p className="mt-0.5 text-xs text-fg-subtle">{t("build.actions.editor.audienceHint", { levels: levels || "…" })}</p>
      <Controller
        control={form.control}
        name="audiences"
        render={({ field }) => (
          <ChoiceChips
            multiple
            aria-label={t("build.actions.editor.audience")}
            className="mt-3"
            options={CODES.map((code) => ({ value: String(code), label: label(code), disabled }))}
            value={field.value.map(String)}
            onValueChange={(next) => { if (next.length > 0) field.onChange(next.map(Number)); }}
          />
        )}
      />
      {form.formState.errors.audiences?.message ? (
        <p role="alert" className="mt-1 text-xs font-semibold text-danger">{form.formState.errors.audiences.message}</p>
      ) : null}
    </div>
  );
}
